import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Menu, Moon, Sun, X } from 'lucide-react';

export interface MobileMenuItem {
  label: string;
  href: string;
  onSelect?: () => void;
}

export function MobileSiteMenu({ items, openWorkspace, theme, toggleTheme }: {
  items: MobileMenuItem[];
  openWorkspace: () => void;
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.body.style.overflow = 'hidden';
    addEventListener('keydown', onKeyDown);
    requestAnimationFrame(() => drawerRef.current?.querySelector<HTMLElement>('a, button')?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = (item: MobileMenuItem, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (item.onSelect) {
      event.preventDefault();
      item.onSelect();
    }
    setOpen(false);
  };

  return <div className="mobile-site-menu">
    <button ref={triggerRef} className="mobile-menu-trigger" type="button" aria-expanded={open} aria-controls="mobile-site-navigation" aria-label={open ? 'Close navigation menu' : 'Open navigation menu'} onClick={() => setOpen((value) => !value)}>
      {open ? <X size={18} /> : <Menu size={18} />}
    </button>
    {open && <div className="mobile-nav-layer" onMouseDown={(event) => { if (event.currentTarget === event.target) setOpen(false); }}>
      <div ref={drawerRef} id="mobile-site-navigation" className="mobile-nav-drawer" role="navigation" aria-label="Mobile navigation">
        <header><span>ORTL / NAVIGATION</span><b>BETA</b></header>
        <div className="mobile-nav-links">
          {items.map((item, index) => <a key={item.label} href={item.href} onClick={(event) => choose(item, event)}><span>{String(index + 1).padStart(2, '0')}</span><b>{item.label}</b><ChevronRight size={14} /></a>)}
        </div>
        <div className="mobile-nav-actions">
          <button type="button" className="mobile-theme-action" onClick={() => { toggleTheme(); setOpen(false); }}>{theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />} Switch to {nextTheme}</button>
          <button type="button" className="mobile-launch-action" onClick={() => { setOpen(false); openWorkspace(); }}>Launch workbench <ChevronRight size={14} /></button>
        </div>
      </div>
    </div>}
  </div>;
}
